import { Link, router } from "@inertiajs/react"
import {
  ArrowDown,
  ArrowUp,
  ChevronUpDouble,
  Eye,
  EyeOff,
} from "@untitledui/icons"

import { Button } from "@/components/ui/button"
import {
  adminCallListEntryPath,
  adminEnquiryPath,
  adminSchoolPath,
} from "@/routes"

import type { CardSummary } from "../types"

import { ChipStrip } from "./chip-strip"
import { TuneModal } from "./tune-modal"

/** One ranked row: the school, why it is here, who to ask for, what happens next. */
export function CallListCard({
  entry,
  listId,
  scopeId,
}: {
  entry: CardSummary
  listId: number
  scopeId: number
}) {
  const patch = (data: Record<string, number | boolean>) =>
    router.patch(adminCallListEntryPath(listId, entry.entry_id), data, {
      preserveScroll: true,
      only: ["entries", "counts"],
    })

  const href =
    entry.id !== null
      ? adminSchoolPath(entry.id)
      : entry.enquiry_id
        ? adminEnquiryPath(entry.enquiry_id)
        : null
  const muted = entry.skipped_today || entry.excluded_for !== null

  return (
    <article
      className={
        "border-secondary bg-primary flex flex-col gap-3 rounded-xl border p-4 " +
        (muted ? "opacity-60" : "")
      }
      aria-label={entry.name}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <span className="text-quaternary w-6 shrink-0 pt-0.5 text-right text-sm tabular-nums">
            {entry.position ?? "—"}
          </span>
          <div className="flex min-w-0 flex-col gap-1">
            {href ? (
              <Link
                href={href}
                className="text-primary hover:text-brand-primary text-md truncate font-semibold underline decoration-transparent transition-colors hover:decoration-current"
              >
                {entry.name}
              </Link>
            ) : (
              <span className="text-primary text-md font-semibold">
                {entry.name}
              </span>
            )}
            <p className="text-tertiary text-sm">{entry.contact_line}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {entry.position !== null && (
            <>
              <Button
                size="sm"
                color="tertiary"
                iconLeading={ChevronUpDouble}
                aria-label="Move to top"
                isDisabled={entry.position === 1}
                onPress={() => patch({ position: 1 })}
              />
              <Button
                size="sm"
                color="tertiary"
                iconLeading={ArrowUp}
                aria-label="Move up"
                onPress={() =>
                  patch({ position: Math.max(1, (entry.position ?? 1) - 1) })
                }
              />
              <Button
                size="sm"
                color="tertiary"
                iconLeading={ArrowDown}
                aria-label="Move down"
                onPress={() => patch({ position: (entry.position ?? 1) + 1 })}
              />
            </>
          )}
          <Button
            size="sm"
            color="tertiary"
            iconLeading={entry.skipped_today ? Eye : EyeOff}
            aria-label={entry.skipped_today ? "Unskip" : "Skip for today"}
            onPress={() => patch({ skip: !entry.skipped_today })}
          />
          {entry.id !== null && (
            <TuneModal
              schoolId={entry.id}
              schoolName={entry.name}
              scopeId={scopeId}
              returnToScope={scopeId}
            />
          )}
        </div>
      </div>
      <ChipStrip chips={entry.chips} dense />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-secondary text-sm">{entry.next_step}</p>
        {entry.tunings.length > 0 && (
          <p className="text-tertiary text-xs">
            {entry.tunings
              .map(
                (t) =>
                  `${t.kind.replace(/_/g, " ")}${t.until ? ` until ${t.until}` : ""}`,
              )
              .join(" · ")}
          </p>
        )}
        {entry.source === "manual" && (
          <span className="text-tertiary text-xs">Added by hand</span>
        )}
      </div>
    </article>
  )
}
